'use client';

import { RegisterSourceNote } from '@/new_components/company-detail/RegisterSourceNote';
import { COMPANY_COUNTRIES, companyCountryCode } from '@/lib/countries';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildCompanyProfileHeroData,
  buildCompanyProfileStatistics,
  formatRegisterNumber,
  getEffectiveCompanyFinancialsData,
  getEffectiveCompanyRegisterData,
} from '@/new_components/company-detail/companyDetailUtils';
import { CompanyProfileSection } from '@/new_components/company-detail/CompanyProfileSection';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import { FinancialDocsCard } from '@/new_components/company-detail/FinancialDocsCard';
import { PeopleTabbedCard } from '@/new_components/company-detail/PeopleTabbedCard';
import { RegisterDocsCard } from '@/new_components/company-detail/RegisterDocsCard';
import { ArrowRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generateCompanySlug } from '@/lib/company-utils';
import { isAdmin, isPro } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useCompanyPreviewData } from '@/hooks/use-company-preview';
import { useLocale } from '@/hooks/use-locale';
import { useSession } from '@/hooks/use-session';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { LocaleLink } from '@/components/locale-link';
import type { CompanySearchResult } from '@/lib/actions/company-search';

interface CompanyPreviewPanelProps {
  previewEuId: string | null;
  onClose: () => void;
  results: CompanySearchResult[];
}

export function CompanyPreviewPanel({
  previewEuId,
  onClose,
  results,
}: CompanyPreviewPanelProps) {
  const [desktop, setDesktop] = useState(false);
  const [displayEuId, setDisplayEuId] = useState<string | null>(null);
  const [entered, setEntered] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const open = !!previewEuId;
  // Retain the last company only for the exit transition. A switch updates
  // content during render so an old company's request cannot restart.
  if (previewEuId && previewEuId !== displayEuId) setDisplayEuId(previewEuId);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    let secondFrame = 0;
    let timer = 0;
    const frame = requestAnimationFrame(() => {
      if (previewEuId && desktop) {
        secondFrame = requestAnimationFrame(() => setEntered(true));
      } else {
        setEntered(false);
        const duration = window.matchMedia('(prefers-reduced-motion: reduce)')
          .matches
          ? 0
          : 300; // Fallback if no transitionend fires; CSS takes 250 ms.
        timer = window.setTimeout(() => setDisplayEuId(null), duration);
      }
    });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(secondFrame);
      window.clearTimeout(timer);
    };
  }, [previewEuId, desktop]);

  useEffect(() => {
    if (!open || !desktop) return;
    const frame = requestAnimationFrame(() =>
      panelRef.current?.focus({ preventScroll: true }),
    );
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[data-network-fullscreen]')) return;
      // Radix overlays can be portalled outside the panel; let them consume
      // the first Escape before closing their containing company preview.
      if (
        event.target instanceof Element &&
        event.target.closest(
          '[data-state="open"][role="menu"], [data-state="open"][role="listbox"], [data-state="open"][role="dialog"], [data-state="open"][data-slot="popover-content"]',
        )
      )
        return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, desktop, onClose]);

  if (!displayEuId || !desktop) return null;
  return (
    <div
      className={cn(
        'hidden lg:flex absolute inset-y-0 right-0 z-30 w-[min(760px,60%)] min-w-0 flex-col border-l bg-background shadow-xl outline-none transition-transform duration-250 ease-out motion-reduce:transition-none motion-reduce:translate-x-0',
        entered ? 'translate-x-0' : 'translate-x-full',
        !open && 'pointer-events-none',
      )}
      onTransitionEnd={(event) => {
        if (
          !open &&
          event.target === event.currentTarget &&
          ['transform', 'translate'].includes(event.propertyName)
        )
          setDisplayEuId(null);
      }}
      inert={!open}
    >
      <PreviewContent
        key={displayEuId}
        euId={displayEuId}
        active={previewEuId === displayEuId}
        row={results.find((row) => row.euId === displayEuId)}
        onClose={onClose}
        panelRef={panelRef}
      />
    </div>
  );
}

function PreviewContent({
  euId,
  active,
  row,
  onClose,
  panelRef,
}: {
  euId: string;
  active: boolean;
  row?: CompanySearchResult;
  onClose: () => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const { data: session, status: sessionStatus } = useSession();
  const viewer = `${session?.user?.sub ?? 'guest'}:${session?.user?.permissions.join(',') ?? ''}`;
  const data = useCompanyPreviewData(
    euId,
    viewer,
    active && sessionStatus !== 'loading',
  );
  const { basicData, contactData, gesellschafterData } = data;
  const country = companyCountryCode(basicData?.register_type, basicData?.eu_id);
  const hasRegisterDocs = COMPANY_COUNTRIES[country].germanRegisterDocuments;
  const registerData = useMemo(
    () => getEffectiveCompanyRegisterData(basicData, data.registerData),
    [basicData, data.registerData],
  );
  const financialsData = useMemo(
    () =>
      getEffectiveCompanyFinancialsData(basicData, data.financialsData, euId),
    [basicData, data.financialsData, euId],
  );
  const companyHistory =
    data.registerData?.company_history ?? basicData?.company_history ?? null;
  const heroData = useMemo(
    () =>
      buildCompanyProfileHeroData(
        basicData,
        registerData,
        companyHistory,
        euId,
      ),
    [basicData, registerData, companyHistory, euId],
  );
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);
  const statistics = useMemo(
    () => buildCompanyProfileStatistics(financialsData, t, fmt),
    [financialsData, t, fmt],
  );
  const insolvency = basicData?.insolvency ?? data.registerData?.insolvency;
  const legalStatus =
    insolvency?.status ??
    (insolvency?.company_status === 'insolvent'
      ? 'insolvent'
      : insolvency?.dissolved === true
        ? 'in_liquidation'
        : 'active');
  const hasGesellschafterliste =
    basicData?.has_gesellschafterliste ||
    gesellschafterData?.has_gesellschafterliste ||
    false;
  const registerDocumentsSource = gesellschafterData?.register_documents?.length
    ? gesellschafterData
    : registerData;
  const name = basicData
    ? heroData.name
    : row?.name ||
      t(
        data.notFound
          ? 'search.preview.notFound'
          : data.error
            ? 'search.preview.error'
            : 'search.preview.loading',
      );
  const slug = row
    ? generateCompanySlug(
        {
          name: row.name,
          sitz: row.city,
          registerCourt: row.registerCourt,
          registerType: row.registerType,
          registerId: row.registerId,
        },
        locale,
      )
    : basicData
      ? generateCompanySlug(
          {
            name: basicData.display_name,
            sitz: basicData.sitz,
            registerCourt: basicData.register_court,
            registerType: basicData.register_type,
            registerId: basicData.register_id,
          },
          locale,
        )
      : null;
  const closeButton = (
    <Button
      variant="ghost"
      size="icon"
      className="size-10 shrink-0"
      onClick={onClose}
      aria-label={t('search.preview.close')}
    >
      <X aria-hidden className="size-5" />
    </Button>
  );

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-label={name}
      className="flex h-full min-h-0 flex-col outline-none"
    >
      {(!basicData || data.isLoading || data.notFound || data.error) && (
        <div className="flex shrink-0 justify-end border-b bg-background px-4 py-3">
          {closeButton}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {data.isLoading ? (
          <div
            role="status"
            aria-label={t('search.preview.loading')}
            className="space-y-4 p-4"
          >
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-56 w-full" />
          </div>
        ) : data.notFound ? (
          <p
            role="status"
            className="py-8 text-center text-sm text-muted-foreground"
          >
            {t('search.preview.notFound')}
          </p>
        ) : data.error ? (
          <p
            role="status"
            className="py-8 text-center text-sm text-muted-foreground"
          >
            {t('search.preview.error')}
          </p>
        ) : (
          basicData && (
            <div>
              <CompanyProfileSection
                compact
                headerAction={closeButton}
                basicData={basicData}
                registerData={registerData}
                financialsData={financialsData}
                contactData={contactData}
                heroData={heroData}
                statistics={statistics}
                isLoadingBackground={data.loadingBackground}
                euId={euId}
                networkData={data.networkData}
                insolvencyRecords={insolvency?.records ?? []}
                legalStatus={legalStatus}
                isDeleted={heroData.isDeleted}
              />
              <div className="space-y-4 p-4">
                {!heroData.isDeleted &&
                (registerData?.participants?.length ||
                  hasGesellschafterliste ||
                  (financialsData?.subsidiaries?.length ?? 0) > 0) ? (
                  <PeopleTabbedCard
                    country={country}
                    participants={registerData?.participants ?? []}
                    gesellschafter={gesellschafterData?.gesellschafter ?? []}
                    hasGesellschafterliste={hasGesellschafterliste}
                    kommanditisten={companyHistory?.kommanditisten ?? []}
                    showKommanditPercent={/[&+]\s*Co\./i.test(
                      basicData.display_name,
                    )}
                    subsidiaries={financialsData?.subsidiaries ?? []}
                    isAuthenticated={!!session}
                    euId={euId}
                    showRegisterAttribution={country !== 'CH'}
                    registerCourt={basicData.register_court}
                    registerId={formatRegisterNumber(
                      basicData.register_type,
                      basicData.register_id,
                    )}
                    capturedAt={basicData.updated_at}
                  />
                ) : null}
                {hasRegisterDocs && (
                  <RegisterDocsCard
                    registerFiles={registerData?.register_files ?? []}
                    registerDocuments={
                      registerDocumentsSource?.register_documents
                    }
                    registerDocumentsComplete={
                      registerDocumentsSource?.register_documents_complete
                    }
                    dkRegisterFile={
                      gesellschafterData?.dk_register_file ??
                      basicData.register_files?.find(
                        (file) =>
                          file.file_type === 'HandelsRegisterDKGesellschafter',
                      ) ??
                      null
                    }
                    hasGesellschafterliste={hasGesellschafterliste}
                    rechtsformCode={
                      registerData?.rechtsform_code ?? basicData.rechtsform_code
                    }
                    gruendungsdokument={
                      registerData?.gruendungsdokument ??
                      basicData.gruendungsdokument
                    }
                    ersteEintragungDatum={
                      registerData?.erste_eintragung_datum ??
                      basicData.erste_eintragung_datum
                    }
                    euId={euId}
                    isAuthenticated={!!session}
                    isRegisterLoading={data.isRegisterLoading && !!session}
                    isGesellschafterLoading={
                      data.isGesellschafterLoading && !!session
                    }
                    isDeleted={heroData.isDeleted}
                    showRegisterAttribution={country !== 'CH'}
                    registerCourt={basicData.register_court}
                    registerId={formatRegisterNumber(
                      basicData.register_type,
                      basicData.register_id,
                    )}
                    capturedAt={basicData.updated_at}
                  />
                )}
                <FinancialDocsCard
                  financialFiles={financialsData?.financial_files ?? []}
                  euId={euId}
                  isAuthenticated={!!session}
                  canDownload={isAdmin(session) || isPro(session)}
                />
                {/* The Firmenbuch's CC BY 4.0 credit travels with its data,
                    so the preview carries it too (the Swiss note stays on the
                    full page only, as before). */}
                {country === 'AT' && (
                  <div className="px-4 pb-4">
                    <RegisterSourceNote country="AT" />
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>
      <div className="sticky bottom-0 shrink-0 border-t bg-background p-4">
        {slug && !data.notFound ? (
          <Button variant="primary" className="w-full" asChild>
            <LocaleLink href={slug}>
              {t('search.preview.openCompanyPage')}
              <ArrowRight aria-hidden className="size-4" />
            </LocaleLink>
          </Button>
        ) : (
          <Button variant="primary" className="w-full" disabled>
            {t('search.preview.openCompanyPage')}
            <ArrowRight aria-hidden className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
